import type { IconName } from '../components/Icon'
import type { LinkDisplayChoice } from './linkDisplay'

/**
 * What can be done with a link, in one list. A pill, a row, the card above a note and an entry on the Links screen all show
 * Open and a ⋯ at most; the ⋯ (or a tap on a pill) opens this list, in this order, in a popover on a desktop and a sheet on a
 * phone (components/LinkActionsMenu.tsx). A surface passes only the actions it can perform: the rest are not offered.
 */
export type LinkActionId = 'open' | 'copy' | 'retry' | 'summary' | 'ask' | 'edit' | 'note' | 'pin' | 'move' | 'keep' | 'remove'

export interface LinkAction {
  id: LinkActionId
  icon: IconName
  label: string
  /** Smaller text at the end of the row (the host, for Open). */
  hint?: string
  /** Open is a real link, so it opens the way every link does. */
  href?: string
  disabled?: boolean
  danger?: boolean
  run: () => void
}

export interface LinkMenuModel {
  /** Groups in their fixed order, each separated by a rule. */
  groups: LinkAction[][]
  /** How this link is shown (Pill, Row, Preview), when the surface lets it change. */
  display?: { value: LinkDisplayChoice; onChange: (choice: LinkDisplayChoice) => void }
}

export interface LinkMenuInput {
  open: { href: string; label: string; host: string }
  onCopy: () => void
  /** Only when the page's details could not be read. */
  onRetry?: () => void
  /** Summaries are on. `state` is what the link already has; `inPlace` is true where Refresh can run right there (the card). */
  summary?: { state: 'none' | 'shown' | 'hidden'; inPlace: boolean; unavailable: boolean; run: () => void }
  ask?: { unavailable: boolean; run: () => void }
  onEdit?: () => void
  onOpenNote?: () => void
  pin?: { pinned: boolean; run: () => void }
  onMove?: () => void
  onRemove?: () => void
  onRemoveLink?: () => void
  display?: LinkMenuModel['display']
}

/** The site a link goes to, as the menu names it beside Open. */
export function linkHost(href: string): string {
  try { return new URL(href).hostname.replace(/^www\./, '') } catch { return '' }
}

const UNAVAILABLE = ' (unavailable)'

export function summaryLabel(summary: NonNullable<LinkMenuInput['summary']>): string {
  const label = summary.state === 'none' ? 'Summarize page'
    : summary.state === 'hidden' || !summary.inPlace ? 'Show summary'
      : 'Refresh summary'
  return summary.unavailable ? `${label}${UNAVAILABLE}` : label
}

export function buildLinkMenu(input: LinkMenuInput): LinkMenuModel {
  const groups: LinkAction[][] = []
  const group = (...actions: Array<LinkAction | null>) => {
    const kept = actions.filter((action): action is LinkAction => action !== null)
    if (kept.length) groups.push(kept)
  }
  group(
    { id: 'open', icon: 'external', label: input.open.label, hint: input.open.host, href: input.open.href, run: () => undefined },
    { id: 'copy', icon: 'copy', label: 'Copy link', run: input.onCopy },
  )
  group(
    input.onRetry ? { id: 'retry', icon: 'repeat', label: 'Retry details', run: input.onRetry } : null,
    input.summary ? { id: 'summary', icon: 'spark', label: summaryLabel(input.summary), disabled: input.summary.unavailable, run: input.summary.run } : null,
    input.ask ? { id: 'ask', icon: 'help', label: `Ask about page${input.ask.unavailable ? UNAVAILABLE : ''}`, disabled: input.ask.unavailable, run: input.ask.run } : null,
  )
  group(
    input.onEdit ? { id: 'edit', icon: 'edit', label: 'Edit label or address', run: input.onEdit } : null,
    input.onOpenNote ? { id: 'note', icon: 'article', label: 'Open the note', run: input.onOpenNote } : null,
    input.pin ? { id: 'pin', icon: 'bump', label: input.pin.pinned ? 'Unpin' : 'Pin to the top', run: input.pin.run } : null,
    input.onMove ? { id: 'move', icon: 'tag', label: 'Move to a topic', run: input.onMove } : null,
  )
  group(
    input.onRemove ? { id: 'keep', icon: 'scissors', label: 'Remove link, keep the words', danger: true, run: input.onRemove } : null,
    input.onRemoveLink ? { id: 'remove', icon: 'trash', label: 'Remove link', danger: true, run: input.onRemoveLink } : null,
  )
  return { groups, display: input.display }
}
